import { useState, useMemo } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common/ui/tabs'
import { ClientTab } from '../types'
import { ClientsTable } from './ClientsTable'
import { InProgressTable } from './InProgressTable'
import { TabCountBadge } from './TabCountBadge'
import { NAVIGATION_COPY } from '@/locales/en'
import type { AccountType, Account } from '@/common/types'
import { formatDisplayName } from '../utils/formatDisplayName'
import { filterBySearch } from '../utils/filterBySearch'

type AccountInfo = {
    accountId: string
    accountName: string
    accountType: AccountType
}

export type ClientsTableWithTabsProps = {
    // Tab state
    activeTab: ClientTab
    onTabChange: (tab: ClientTab) => void
    // Active tab data
    activeAccounts?: AccountInfo[]
    activeCount?: number
    // In Progress tab data
    inProgressClients?: Account[]
    inProgressCount?: number
    // Shared props
    activeAccountId?: string | null
    onAddClient?: () => void
    onSelectAccount?: (accountId: string) => void
    onResumeClient?: (accountId: string) => void
}

export function ClientsTableWithTabs({
    activeTab,
    onTabChange,
    activeAccounts = [],
    activeCount,
    inProgressClients = [],
    inProgressCount,
    activeAccountId = null,
    onAddClient = () => {},
    onSelectAccount = () => {},
    onResumeClient = () => {},
}: ClientsTableWithTabsProps) {
    // Derive counts from arrays if not provided explicitly
    const displayActiveCount = activeCount ?? activeAccounts.length
    const displayInProgressCount = inProgressCount ?? inProgressClients.length

    // Shared search state - persists across tab changes
    const [searchTerm, setSearchTerm] = useState('')

    // Filter active accounts based on search
    const filteredActiveAccounts = useMemo(
        () =>
            filterBySearch(activeAccounts, searchTerm, (account) => [
                account.accountName,
                account.accountId,
                NAVIGATION_COPY.accountTypes[account.accountType],
            ]),
        [activeAccounts, searchTerm],
    )

    // Filter in-progress clients based on search
    const filteredInProgressClients = useMemo(
        () =>
            filterBySearch(inProgressClients, searchTerm, (client) => {
                const { firstName, lastName } = formatDisplayName(client.accountName)
                return [firstName, lastName, client.accountId, NAVIGATION_COPY.accountTypes[client.accountType]]
            }),
        [inProgressClients, searchTerm],
    )

    // Generate count labels
    const activeCountLabel = useMemo(
        () =>
            NAVIGATION_COPY.adviserClients.foundCount
                .replace('{count}', String(filteredActiveAccounts.length))
                .replace('{plural}', filteredActiveAccounts.length === 1 ? '' : 's'),
        [filteredActiveAccounts.length],
    )

    const inProgressCountLabel = useMemo(
        () =>
            NAVIGATION_COPY.adviserClients.foundCount
                .replace('{count}', String(filteredInProgressClients.length))
                .replace('{plural}', filteredInProgressClients.length === 1 ? '' : 's'),
        [filteredInProgressClients.length],
    )

    const handleTabChange = (value: string) => {
        onTabChange(value as ClientTab)
    }

    return (
        <div className="w-full bg-white rounded-tl-lg rounded-tr-lg flex flex-col">
            <div className="flex items-center justify-between gap-6 flex-wrap mb-6">
                <h1 className="text-black text-4xl font-normal font-['Tangerine'] leading-10">
                    {NAVIGATION_COPY.adviserClients.title}
                </h1>
            </div>

            <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList variant="underline" className="w-full md:max-w-[300px]">
                    <TabsTrigger variant="underline" value={ClientTab.ACTIVE}>
                        <div className="inline-flex items-center gap-2">
                            {NAVIGATION_COPY.adviserClients.tabs.active}
                            <TabCountBadge
                                count={displayActiveCount}
                                variant={activeTab === ClientTab.ACTIVE ? 'active' : 'inactive'}
                            />
                        </div>
                    </TabsTrigger>
                    <TabsTrigger variant="underline" value={ClientTab.IN_PROGRESS}>
                        <div className="inline-flex items-center gap-2">
                            {NAVIGATION_COPY.adviserClients.tabs.inProgress}
                            <TabCountBadge
                                count={displayInProgressCount}
                                variant={activeTab === ClientTab.IN_PROGRESS ? 'active' : 'inactive'}
                            />
                        </div>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value={ClientTab.ACTIVE} className="mt-6">
                    <ClientsTable
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        filteredAccounts={filteredActiveAccounts}
                        activeAccountId={activeAccountId}
                        hasClientAccounts={activeAccounts.length > 0}
                        clientCountLabel={activeCountLabel}
                        showClientCount={true}
                        onAddClient={onAddClient}
                        onSelectAccount={onSelectAccount}
                    />
                </TabsContent>

                <TabsContent value={ClientTab.IN_PROGRESS} className="mt-6">
                    <InProgressTable
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        clients={filteredInProgressClients}
                        clientCountLabel={inProgressCountLabel}
                        showClientCount={true}
                        onResume={onResumeClient}
                        onAddClient={onAddClient}
                    />
                </TabsContent>
            </Tabs>
        </div>
    )
}
